import React, {
  useDeferredValue, useEffect, useMemo, useState,
} from "react"
import { Prompt } from "react-router-dom"
import {
  Divider, Flex, MenuItemOption, MenuOptionGroup,
} from "@chakra-ui/react"
import {
  DioboxEvent, Form, PreviewSelectOptions,
} from "sharedTypes"
import Menu from "components/dialogs/Menu"
import FloatingToolbar, { FloatingToolbarField } from "components/elements/FloatingToolbar"
import DeviceToggle, { PreviewDevice } from "components/elements/DeviceToggle"
import { successToast } from "utilities/toasts"
import ColorThemePicker from "../ColorTheme/ColorThemePicker"
import { PortalColors, pickPortalColors, sameColors } from "../ColorTheme/themes"
import PreviewContent, { formTypeByOption } from "./PreviewContent"
import PreviewStage from "./PreviewStage"
import { StyleAndConfigurationModalName } from "../StyleAndConfigurationModals/StyleAndConfigurationModals"

type Props = {
  forms: Form[]
  event: DioboxEvent
  onSelectModal: (modalName: StyleAndConfigurationModalName | null) => void
  updateEvent: (params: Partial<DioboxEvent>) => Promise<void>
}

const PortalPreview = ({
  forms,
  event,
  onSelectModal,
  updateEvent,
}: Props) => {
  const savedColors = pickPortalColors(event)
  const [draftColors, setDraftColors] = useState<PortalColors | null>(null)
  const hasUnsavedColors = draftColors !== null && !sameColors(draftColors, savedColors)
  useEffect(() => {
    if (!hasUnsavedColors) {
      return undefined
    }
    const warnBeforeLeaving = (unloadEvent: BeforeUnloadEvent) => unloadEvent.preventDefault()
    window.addEventListener("beforeunload", warnBeforeLeaving)

    return () => window.removeEventListener("beforeunload", warnBeforeLeaving)
  }, [hasUnsavedColors])

  const previewColors = useDeferredValue(draftColors)
  const previewEvent = useMemo(
    () => (previewColors ? { ...event, ...previewColors } : event),
    [event, previewColors],
  )

  const saveColors = async () => {
    if (!draftColors) {
      return
    }
    const savedDraft = draftColors
    await updateEvent(savedDraft)
    successToast({ title: "Color Theme Saved" })
    setDraftColors((current) => (current === savedDraft ? null : current))
  }

  const [device, setDevice] = useState<PreviewDevice>("desktop")
  const [selectedOption, setSelectedOption] = useState<PreviewSelectOptions>(
    PreviewSelectOptions.FormSelectionPage,
  )

  const availableFormTypes = new Set(forms.map((f) => f.type))

  const renderMenuItems = () => Object.values(PreviewSelectOptions).map((option, index) => {
    const requiredFormType = formTypeByOption[option]
    const isDisabled = requiredFormType ? !availableFormTypes.has(requiredFormType) : false

    const item = (
      <MenuItemOption key={option} value={option} py={3} isDisabled={isDisabled}>
        {option}
      </MenuItemOption>
    )

    return index === 1 ? [item, <Divider key="divider" />] : item
  })

  const content = (
    <PreviewContent
      event={previewEvent}
      selectedOption={selectedOption}
      forms={forms}
      onSelectModal={onSelectModal}
    />
  )

  const toolbar = (compact: boolean) => (
    <Flex gap={3}>
      <FloatingToolbar flex={1} minW={0}>
        <FloatingToolbarField label="Preview" hideLabel={compact}>
          <Menu
            noPadding
            placement="bottom-start"
            buttonText={selectedOption}
            buttonVariant="ghost"
            buttonColor="black"
            buttonFontWeight="normal"
            menuItems={[
              <MenuOptionGroup
                key="menu-options"
                defaultValue={selectedOption}
                type="radio"
                onChange={(value) => setSelectedOption(value as PreviewSelectOptions)}
              >
                {renderMenuItems()}
              </MenuOptionGroup>,
            ]}
          />
        </FloatingToolbarField>
        <DeviceToggle value={device} onChange={setDevice} />
      </FloatingToolbar>
      <FloatingToolbar flexShrink={0}>
        <FloatingToolbarField label="Color Theme" hideLabel={compact}>
          <ColorThemePicker
            colors={draftColors ?? savedColors}
            onChange={setDraftColors}
            hasChanges={hasUnsavedColors}
            onSave={saveColors}
            onCancel={() => setDraftColors(null)}
          />
        </FloatingToolbarField>
      </FloatingToolbar>
    </Flex>
  )

  return (
    <Flex direction="column" position="relative" w="full" h="full" backgroundColor="gray.50">
      <Prompt
        when={hasUnsavedColors}
        message="Are you sure you want to leave this page? You have some unsaved changes."
      />
      <PreviewStage
        device={device}
        toolbar={toolbar}
        topBackground={previewEvent.portalTopNavBackgroundColor}
        background={previewEvent.portalBackgroundColor}
      >
        {content}
      </PreviewStage>
    </Flex>
  )
}

export default PortalPreview
